import React, { ReactNode, useState } from 'react';
import {
  $ExportProfileOptions,
  $ProfileExportableColumns,
  ExportProfileOptions,
} from '@loyalty/shared-common/manager-api-client';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette, useAlerts } from '@qualifio/ui-commons';
import { faCloudArrowDown } from '@fortawesome/pro-solid-svg-icons/faCloudArrowDown';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CurrencyImage } from '../currency/CurrencyImage';
import { Loader } from '../layout/Loader';
import { Title } from '../layout/Title';
import { useApi } from '../api-client/ApiClientProvider';
import { useCurrencies } from '../../currency/CurrenciesContext';
import { useCustomKeys } from '../../custom-keys/CustomKeysContext';
import { useProgram } from '../../loyalty/ProgramContext';

const $FormValues = z.object({
  columns: $ExportProfileOptions.shape.columns, // reuse existing columns schema
  relations: z.object({
    currencies: z.array(z.object({ id: z.string(), enabled: z.boolean() })),
    customKeys: z.array(z.object({ id: z.string(), enabled: z.boolean() })),
  }),
});

type FormValues = z.infer<typeof $FormValues>;

function LabeledCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
}) {
  return (
    <FormControlLabel
      sx={{
        border: `1px solid ${colorPalette.grey.main}`,
        minWidth: '200px',
        px: 1.5,
        display: 'flex',
        justifyContent: 'flex-start',
        marginLeft: 0,
      }}
      control={<Checkbox checked={checked} onChange={e => onChange(e.target.checked)} />}
      label={label}
    />
  );
}

function ColumnList({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '2px' }}>
      {children}
    </Box>
  );
}

export function ExportProfiles() {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { managerApi } = useApi();
  const { program } = useProgram();
  const { customKeys } = useCustomKeys();
  const { currencies } = useCurrencies();

  const [isOpen, setIsOpen] = useState(false);

  const exportProfiles = useMutation({
    mutationKey: ['programs', program.id, 'export-profiles'],
    mutationFn: (options: ExportProfileOptions) =>
      managerApi.exportProfiles(program.id, { params: options }),
    onSuccess: () => setIsOpen(false),
    onError: () => notify(t('generic.error'), 'error'),
  });

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver($FormValues),
    defaultValues: {
      columns: {
        general: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          language: true,
          birthday: false,
          gender: false,
          phone: false,
        },
        address: {
          state: false,
          country: false,
          city: false,
          zipCode: false,
          street: false,
          number: false,
          box: false,
        },
        loyalty: {
          levelName: true,
          createdAt: true,
          lastVisit: true,
          referrerId: false,
          rewardsCount: false,
          memberCode: false,
          referralCode: false,
        },
      },
      relations: {
        currencies: currencies.map((c, i) => ({ id: c.id, enabled: i === 0 })), // first one on, rest off
        customKeys: customKeys.map(ck => ({ id: ck.technicalName, enabled: false })),
      },
    },
  });

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        color="blue"
        disabled={exportProfiles.isPending}
        startIcon={
          exportProfiles.isPending ? <Loader /> : <FontAwesomeIcon icon={faCloudArrowDown} />
        }
      >
        {t('profiles.actions.export')}
      </Button>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth={'md'}
        fullWidth
        sx={{
          '& .MuiDialogContent-root': {
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '2px',
            '&:after': {
              marginTop: 0,
            },
          },
        }}
      >
        <IconButton
          onClick={() => setIsOpen(false)}
          sx={{
            zIndex: 2,
            position: 'absolute',
            right: 5,
            top: 4,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <form
          onSubmit={form.handleSubmit(data => {
            exportProfiles.mutate({
              columns: data.columns,
              relations: {
                currencies: data.relations.currencies.filter(x => x.enabled).map(x => x.id),
                customKeys: data.relations.customKeys.filter(x => x.enabled).map(x => x.id),
              },
            });
          })}
        >
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Title>{t('profiles.export.dialog.title')}</Title>
            <p>{t('profiles.export.dialog.description')}</p>

            <div style={{ display: 'flex', gap: '40px', marginBottom: '40px' }}>
              <ColumnList>
                <h3>{t('profiles.export.dialog.generalColumns')}</h3>
                {$ProfileExportableColumns.shape.general.keyof().options.map(col => (
                  <Controller
                    key={col}
                    control={form.control}
                    name={`columns.general.${col}`}
                    render={({ field: { value, onChange } }) => (
                      <LabeledCheckbox
                        checked={!!value}
                        onChange={onChange}
                        label={t(`profiles.export.columns.${col}`)}
                      />
                    )}
                  />
                ))}
              </ColumnList>

              <ColumnList>
                <h3>{t('profiles.export.dialog.address')}</h3>
                {$ProfileExportableColumns.shape.address.keyof().options.map(col => (
                  <Controller
                    key={col}
                    control={form.control}
                    name={`columns.address.${col}`}
                    render={({ field: { value, onChange } }) => (
                      <LabeledCheckbox
                        checked={!!value}
                        onChange={onChange}
                        label={t(`profiles.export.columns.${col}`)}
                      />
                    )}
                  />
                ))}
              </ColumnList>

              <ColumnList>
                <h3>{t('profiles.export.dialog.loyalty')}</h3>
                {$ProfileExportableColumns.shape.loyalty.keyof().options.map(col => (
                  <Controller
                    key={col}
                    control={form.control}
                    name={`columns.loyalty.${col}`}
                    render={({ field: { value, onChange } }) => (
                      <LabeledCheckbox
                        checked={!!value}
                        onChange={onChange}
                        label={t(`profiles.export.columns.${col}`)}
                      />
                    )}
                  />
                ))}
              </ColumnList>
            </div>
            <div style={{ display: 'flex', gap: '40px', marginBottom: '20px' }}>
              <ColumnList>
                <h3>{t('topbar.tabs.settings.currency')}</h3>
                <div style={{ maxHeight: '20vh', overflowY: 'auto', flexShrink: 0 }}>
                  {currencies.map((currency, i) => (
                    <Controller
                      key={currency.id}
                      control={form.control}
                      name={`relations.currencies.${i}.enabled`}
                      render={({ field: { value, onChange } }) => (
                        <LabeledCheckbox
                          checked={value}
                          onChange={onChange}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CurrencyImage currency={currency} sx={{ mr: '5px' }} />
                              {getLocalizedProperty(currency, 'name')}
                            </Box>
                          }
                        />
                      )}
                    />
                  ))}
                </div>
              </ColumnList>

              {customKeys.length > 0 && (
                <ColumnList>
                  <h3>{t('topbar.tabs.custom-keys')}</h3>
                  <div style={{ maxHeight: '20vh', overflowY: 'auto', flexShrink: 0 }}>
                    {customKeys.map((ck, i) => (
                      <Controller
                        key={ck.technicalName}
                        control={form.control}
                        name={`relations.customKeys.${i}.enabled`}
                        render={({ field: { value, onChange } }) => (
                          <LabeledCheckbox checked={value} onChange={onChange} label={ck.name} />
                        )}
                      />
                    ))}
                  </div>
                </ColumnList>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button type="button" color="grey" onClick={() => setIsOpen(false)}>
              {t('generic.actions.cancel')}
            </Button>
            <Button
              color="success"
              type="submit"
              disabled={exportProfiles.isPending}
              startIcon={exportProfiles.isPending ? <Loader /> : undefined}
            >
              {t('profiles.actions.export')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
